import React, { useEffect, useRef, useState } from "react";
import { TextField, Theme, useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { CampaignSideNavStep } from "../layout/CampaignBuilderSideNav";
import { useAppDispatch } from "../../../../store/store";
import { useResetToDraft } from "../../../../hooks/useResetToDraft";
import { useLanguageTheme } from "../../../../hooks/useLanguageTheme";
import { setAboutDeveloper } from "../../../../store/features/campaignBuilderSlice";
import { Errors, textFieldStyles } from "./MainInformationStep";
import { CampaignBuilderSideNavHeader } from "../layout/CampaignBuilderSideNavHeader";
import { LangToggle } from "../../../_shared/LangToggle";
import { CampainBuilderNextBtn } from "../buttons/CampainBuilderNextBtn";
import {
  MAX_ABOUT_DEVELOPER_LENGTH_EN,
  MAX_ABOUT_DEVELOPER_LENGTH_HE,
  MIN_ABOUT_DEVELOPER_LENGTH_EN,
  MIN_ABOUT_DEVELOPER_LENGTH_HE,
} from "../../constants";
import { BackToMenuBtn } from "../buttons/BackToMenuBtn";
import AiContextMenu from "../../ai-chat/AiContextMenu";
import { useContextMenu } from "react-contexify";
import { CampainBuilderBackBtn } from "../buttons/CampainBuilderBackBtn";

export const AboutDeveloper = ({
  setCurrentStep,
  allFieldsErrors,
  setAllFieldsErrors,
  hasPublish,
  autosaveFunc = () => {},
  isDisabledSwitchLang = false,
}: {
  setCurrentStep: (step: CampaignSideNavStep) => void;
  allFieldsErrors: any;
  setAllFieldsErrors: any;
  hasPublish: boolean;
  autosaveFunc: any;
  isDisabledSwitchLang: boolean;
}) => {
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation(["campaignBuilder"]);
  const isRTL = i18n.dir() === "rtl";
  const resetToDraft = useResetToDraft();
  const textFieldRef = useRef(null);
  const downMd = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const langTheme = useLanguageTheme();
  const [errors, setErrors] = React.useState<any>({});
  const [hasSubmitted, setHasSubmitted] = React.useState(false);

  const { aboutDeveloper } = useSelector((state: any) => state.campaignBuilder);
  const currentLanguage = i18n.language;
  React.useEffect(() => {
    if (allFieldsErrors.about_developer) {
      const validationErrors = validateFields();

      setErrors(validationErrors);
    }
  }, []);

  React.useEffect(() => {
    const newAllErrors = { ...allFieldsErrors };

    if (Object.keys(errors).length === 0) {
      delete newAllErrors.about_developer;
    } else {
      newAllErrors.about_developer = "about_developer";
    }
    setAllFieldsErrors(newAllErrors);
  }, [errors]);

  const handleChangePropertyDesc = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newErrors = { ...errors };
    const value = event.target.value;
    dispatch(setAboutDeveloper(value));
    resetToDraft();
    localStorage.setItem("onChangeCompanyBuilder", "1");
    if (hasSubmitted || hasPublish) {
      if (value.trim() === "") {
        newErrors.aboutDeveloper = t("errors.desc_is_required");
      } else if (
        value.trim().length <
        (isRTL ? MIN_ABOUT_DEVELOPER_LENGTH_HE : MIN_ABOUT_DEVELOPER_LENGTH_EN)
      ) {
        newErrors.aboutDeveloper = isRTL
          ? t("errors.min_100_chars_required")
          : t("errors.min_150_chars_required");
      } else {
        delete newErrors.aboutDeveloper;
      }
      setErrors(newErrors);
    }
  };

  const validateFields = () => {
    let errors: Partial<Errors> = {};

    if (aboutDeveloper.trim() === "") {
      errors.aboutDeveloper = t("errors.desc_is_required");
    } else if (
      aboutDeveloper.trim().length <
      (isRTL ? MIN_ABOUT_DEVELOPER_LENGTH_HE : MIN_ABOUT_DEVELOPER_LENGTH_EN)
    ) {
      errors.aboutDeveloper = isRTL
        ? t("errors.min_100_chars_required")
        : t("errors.min_150_chars_required");
    }

    return errors;
  };

  const handleNext = (step: CampaignSideNavStep) => {
    setHasSubmitted(true);
    const validationErrors = validateFields();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setCurrentStep(step);
    } else {
      setCurrentStep(step);
    }
  };

  const { show } = useContextMenu({ id: "aiFieldContextMenu" });
  const displayMenu = (e: any, key: string, value: any, hover: any) => {
    const offsetX = currentLanguage === "he" ? 230 : 0;
    show({
      event: e,
      props: {
        key: key,
        value: value,
      },
      position: hover
        ? {
            x: Math.max(e.clientX - offsetX, 0),
            y: e.clientY,
          }
        : {
            x: currentLanguage === "he" ? 0 : window.innerWidth - 240,
            y: textFieldRef.current
              ? (textFieldRef.current as HTMLTextAreaElement)?.offsetHeight +
                  (textFieldRef.current as HTMLTextAreaElement)?.clientHeight +
                  30 || 0
              : 0,
          },
    });
  };

  return (
    <div className="campaign-builder-side-nav-wrapper">
      <CampaignBuilderSideNavHeader
        header="about_developer"
        leftBtn={
          <CampainBuilderBackBtn
            setCurrentStep={setCurrentStep}
            step={CampaignSideNavStep.keyFeatures}
          />
        }
        rightBtn={
          <CampainBuilderNextBtn
            handleNext={handleNext}
            step={CampaignSideNavStep.contactInformation}
          />
        }
      />
      <AiContextMenu />

      <div
        className="campaign-builder-sidenav-main-content-wrapper"
        style={{
          paddingTop: downMd ? "30px" : "40px",
          direction: i18n.dir(),
        }}
      >
        <div className="campaign-builder-sidenav-settings-fileds-container">
          <div className="campaign-builder-sidenav-settings-fileds-item">
            <div className="campaign-builder-sidenav-field-name">
              {t("message")}
            </div>
            <div style={{ position: "relative" }}>
              <TextField
                inputRef={textFieldRef}
                fullWidth
                multiline
                rows={6.4}
                placeholder={t("about_developer_placeholder")}
                sx={{
                  ...textFieldStyles(
                    langTheme.mainFont(),
                    errors.aboutDeveloper
                  ),
                  "& .MuiInputBase-root": {
                    alignItems: "flex-start",
                    gap: "3px",
                    paddingBottom: "40px",
                  },
                  "& textarea": {
                    scrollbarWidth: "thin",
                    "&::-webkit-scrollbar": {
                      width: "3px",
                    },
                    "&::-webkit-scrollbar-track": {
                      background: "transparent",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "gray",
                      borderRadius: "4px",
                    },
                  },
                }}
                autoComplete="off"
                value={t(aboutDeveloper)}
                onChange={handleChangePropertyDesc}
                onMouseEnter={(e) =>
                  displayMenu(e, "aboutDeveloper", aboutDeveloper, false)
                }
                onContextMenu={(e) =>
                  displayMenu(e, "aboutDeveloper", aboutDeveloper, true)
                }
                inputProps={{
                  maxLength: isRTL
                    ? MAX_ABOUT_DEVELOPER_LENGTH_HE
                    : MAX_ABOUT_DEVELOPER_LENGTH_EN,
                }}
              />

              <div
                style={{
                  position: "absolute",
                  bottom: "13px",
                  right: isRTL ? "auto" : "27px",
                  left: isRTL ? "27px" : "auto",
                  fontFamily: "DM Sans",
                  color: "#838A9E",
                  padding: "2px 4px",
                  borderRadius: "4px",
                  pointerEvents: "none",
                  fontSize: "14px",
                }}
              >
                {aboutDeveloper.trim().length}/
                {isRTL
                  ? MAX_ABOUT_DEVELOPER_LENGTH_HE
                  : MAX_ABOUT_DEVELOPER_LENGTH_EN}
              </div>
            </div>

            {errors.aboutDeveloper && (
              <div className="campaign-builder-errors">
                {errors.aboutDeveloper}
              </div>
            )}
          </div>
        </div>
        <BackToMenuBtn setCurrentStep={setCurrentStep} />
      </div>
    </div>
  );
};
